import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getProtocolsPageData,
  createInspectionProtocol,
  updateInspectionProtocol,
  deleteInspectionProtocol,
  finalizeInspectionProtocol,
  createActionProtocol,
  createComponent,
  uploadFile,
  exportProtocolToPDF,
  BUILDING_PARTS,
  InspectionItem,
  InspectionProtocol,
  ActionProtocol,
  Component,
  Property,
  Apartment,
  ProtocolStats
} from '../lib/api';
import { 
  ClipboardText,
  Plus,
  MagnifyingGlass,
  FunnelSimple,
  PencilSimple,
  Trash,
  CheckCircle,
  Warning,
  House,
  Buildings,
  Calendar,
  User,
  CaretDown,
  CaretRight,
  Eye,
  File,
  Wrench,
  ArrowRight,
  X,
  Camera,
  CurrencyCircleDollar,
  Image,
  CloudArrowUp,
  FilePdf,
  Printer
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

// Status configuration
const STATUS_CONFIG = {
  ok: { label: 'OK', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  minor_defect: { label: 'Mindre brist', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Warning },
  action_required: { label: 'Åtgärd krävs', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Warning },
  urgent: { label: 'Akut', color: 'bg-red-100 text-red-700 border-red-200', icon: Warning }
};

const PRIORITY_CONFIG = {
  low: { label: 'Låg', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medel', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Hög', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Akut', color: 'bg-red-100 text-red-700' }
};

const INSPECTION_TYPES = [
  { value: 'annual', label: 'Årskontroll' },
  { value: 'move_in', label: 'Inflyttningsbesiktning' },
  { value: 'move_out', label: 'Utflyttningsbesiktning' },
  { value: 'damage', label: 'Skadekontroll' },
  { value: 'moisture', label: 'Fuktkontroll' },
  { value: 'safety', label: 'Säkerhetskontroll' },
  { value: 'other', label: 'Övrig' }
];

export default function ProtocolsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('inspections');
  const [protocols, setProtocols] = useState<InspectionProtocol[]>([]);
  const [actions, setActions] = useState<ActionProtocol[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [stats, setStats] = useState<ProtocolStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInspectionDialog, setShowInspectionDialog] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showComponentDialog, setShowComponentDialog] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState<InspectionProtocol | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState<number | null>(null); // Track which item is uploading
  
  // Protocol form
  const [protocolForm, setProtocolForm] = useState<Partial<InspectionProtocol>>({
    type: 'annual',
    property_id: '',
    apartment_id: '',
    inspection_date: new Date().toISOString().split('T')[0],
    inspector_name: '',
    notes: '',
    items: []
  });
  
  // Action form
  const [actionForm, setActionForm] = useState({
    title: '',
    action_type: '',
    component_id: '',
    property_id: '',
    apartment_id: '',
    action_date: new Date().toISOString().split('T')[0],
    performed_by_name: '',
    description: '',
    cost: '',
    new_status: 'ok',
    notes: ''
  });
  
  // Component form
  const [componentForm, setComponentForm] = useState({
    name: '',
    building_part: 'facade',
    property_id: '',
    apartment_id: '',
    material: '',
    installation_year: '',
    expected_lifespan: '',
    location: '',
    notes: ''
  });

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // Single batch request for all data
      const data = await getProtocolsPageData(user?.organization_id ?? null);
      setProtocols(data.protocols || []);
      setActions(data.actions || []);
      setComponents(data.components || []);
      setProperties(data.properties || []);
      setApartments(data.apartments || []);
      setStats(data.stats || null);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter protocols
  const filteredProtocols = useMemo(() => {
    let filtered = protocols;
    
    if (filterProperty !== 'all') {
      filtered = filtered.filter(p => p.property_id === filterProperty);
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.type?.toLowerCase().includes(query) ||
        p.inspector_name?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [protocols, filterProperty, filterStatus, searchQuery]);

  // Filter actions
  const filteredActions = useMemo(() => {
    let filtered = actions;
    
    if (filterProperty !== 'all') {
      filtered = filtered.filter(a => a.property_id === filterProperty);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.title?.toLowerCase().includes(query) ||
        a.action_type?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [actions, filterProperty, searchQuery]);

  const handleCreateProtocol = async () => {
    if (!protocolForm.property_id && !protocolForm.apartment_id) {
      toast.error('Välj fastighet eller lägenhet');
      return;
    }
    
    setSaving(true);
    try {
      // Initialize items based on building parts
      const initialItems: InspectionItem[] = BUILDING_PARTS.map(part => ({
        building_part: part.id,
        building_part_name: part.name,
        status: 'ok' as const,
        comment: '',
        images: [],
        recommended_action: '',
        priority: 'low' as const,
        estimated_cost: undefined
      }));
      
      await createInspectionProtocol({
        ...protocolForm,
        items: initialItems,
        organization_id: user?.organization_id
      });
      toast.success('Protokoll skapat');
      setShowCreateDialog(false);
      resetProtocolForm();
      fetchData();
    } catch (error) {
      toast.error('Kunde inte skapa protokoll');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenInspection = (protocol: InspectionProtocol): void => {
    setSelectedProtocol(protocol);
    setProtocolForm({
      ...protocol,
      items: protocol.items || BUILDING_PARTS.map(part => ({
        building_part: part.id,
        building_part_name: part.name,
        status: 'ok',
        comment: '',
        images: [],
        recommended_action: '',
        priority: 'low',
        estimated_cost: undefined
      }))
    });
    setShowInspectionDialog(true);
  };

  const handleSaveInspection = async (finalize = false) => {
    setSaving(true);
    try {
      if (finalize) {
        await finalizeInspectionProtocol(selectedProtocol!.id, protocolForm, {
          createMaintenanceActions: true,
          finalized_by: user?.id
        });
        toast.success('Protokoll slutfört och underhållsåtgärder skapade');
      } else {
        await updateInspectionProtocol(selectedProtocol!.id, protocolForm);
        toast.success('Protokoll sparat');
      }
      setShowInspectionDialog(false);
      setSelectedProtocol(null);
      fetchData();
    } catch (error) {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProtocol = async (protocol: InspectionProtocol): Promise<void> => {
    if (!confirm(`Ta bort protokoll från ${formatDate(protocol.inspection_date)}?`)) return;
    
    try {
      await deleteInspectionProtocol(protocol.id);
      toast.success('Protokoll borttaget');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const handleCreateAction = async (): Promise<void> => {
    if (!actionForm.title || !actionForm.action_date) {
      toast.error('Fyll i titel och datum');
      return;
    }
    
    setSaving(true);
    try {
      await createActionProtocol({
        ...actionForm,
        cost: actionForm.cost ? parseFloat(actionForm.cost) : undefined,
        organization_id: user?.organization_id,
        performed_by: user?.id
      });
      toast.success('Åtgärd registrerad');
      setShowActionDialog(false);
      resetActionForm();
      fetchData();
    } catch (error) {
      toast.error('Kunde inte registrera åtgärd');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateComponent = async () => {
    if (!componentForm.name || !componentForm.building_part) {
      toast.error('Fyll i namn och byggnadsdel');
      return;
    }
    
    setSaving(true);
    try {
      await createComponent({
        ...componentForm,
        organization_id: user?.organization_id
      });
      toast.success('Komponent tillagd');
      setShowComponentDialog(false);
      resetComponentForm();
      fetchData();
    } catch (error) {
      toast.error('Kunde inte lägga till komponent');
    } finally {
      setSaving(false);
    }
  };

  const updateProtocolItem = (index: number, field: string, value: unknown): void => {
    const newItems = [...(protocolForm.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    setProtocolForm(prev => ({ ...prev, items: newItems }));
  };

  // Handle image upload for protocol items
  const handleImageUpload = async (index: number, file: File): Promise<void> => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Endast bildfiler tillåtna');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bilden får max vara 10MB');
      return;
    }
    
    setUploadingImage(index);
    try {
      const result = await uploadFile(file);
      const currentImages = protocolForm.items?.[index]?.images || [];
      updateProtocolItem(index, 'images', [...currentImages, {
        url: result.url,
        name: file.name,
        uploaded_at: new Date().toISOString()
      }]);
      toast.success('Bild uppladdad');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Kunde inte ladda upp bilden');
    } finally {
      setUploadingImage(null);
    }
  };

  // Remove image from protocol item
  const removeImage = (itemIndex: number, imageIndex: number): void => {
    const currentImages = protocolForm.items?.[itemIndex]?.images || [];
    const newImages = currentImages.filter((_, i) => i !== imageIndex);
    updateProtocolItem(itemIndex, 'images', newImages);
  };

  const resetProtocolForm = () => {
    setProtocolForm({
      type: 'annual',
      property_id: '',
      apartment_id: '',
      inspection_date: new Date().toISOString().split('T')[0],
      inspector_name: '',
      notes: '',
      items: []
    });
  };

  const resetActionForm = () => {
    setActionForm({
      title: '',
      action_type: '',
      component_id: '',
      property_id: '',
      apartment_id: '',
      action_date: new Date().toISOString().split('T')[0],
      performed_by_name: '',
      description: '',
      cost: '',
      new_status: 'ok',
      notes: ''
    });
  };

  const resetComponentForm = () => {
    setComponentForm({
      name: '',
      building_part: 'facade',
      property_id: '',
      apartment_id: '',
      material: '',
      installation_year: '',
      expected_lifespan: '',
      location: '',
      notes: ''
    });
  };

  const getPropertyName = (id: string | undefined): string => properties.find(p => p.id === id)?.name || '';
  const getApartmentNumber = (id: string | undefined): string => apartments.find(a => a.id === id)?.apartment_number || '';
  const getInspectionTypeLabel = (type: string | undefined): string => INSPECTION_TYPES.find(t => t.value === type)?.label || type || '';
  const formatDate = (dateStr: string | undefined): string => dateStr ? new Date(dateStr).toLocaleDateString('sv-SE') : '-';
  const formatCurrency = (amount: number | string | undefined): string => amount ? `${parseFloat(String(amount)).toLocaleString('sv-SE')} kr` : '-';

  const canEdit = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'board_member';

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-slate-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="protocols-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-indigo-100 flex items-center justify-center">
            <ClipboardText size={20} className="text-indigo-600" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Protokoll</h1>
            <p className="text-sm text-slate-500">Inspektioner, åtgärder och komponenter</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card-flat">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <ClipboardText size={20} className="text-indigo-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{stats.total_inspections}</div>
                <div className="text-xs text-slate-500">Inspektioner</div>
              </div>
            </div>
          </div>
          
          <div className="card-flat">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Wrench size={20} className="text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{stats.total_actions}</div>
                <div className="text-xs text-slate-500">Åtgärder</div>
              </div>
            </div>
          </div>
          
          <div className="card-flat">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Warning size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {(stats.components_action || 0) + (stats.components_urgent || 0)}
                </div>
                <div className="text-xs text-slate-500">Kräver åtgärd</div>
              </div>
            </div>
          </div>
          
          <div className="card-flat">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <CurrencyCircleDollar size={20} className="text-slate-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{formatCurrency(stats.total_cost)}</div>
                <div className="text-xs text-slate-500">Total kostnad</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="inspections">Inspektioner</TabsTrigger>
            <TabsTrigger value="actions">Åtgärder</TabsTrigger>
            <TabsTrigger value="components">Komponenter</TabsTrigger>
          </TabsList>
          
          {canEdit && (
            <div className="flex gap-2">
              {activeTab === 'inspections' && (
                <Button onClick={() => setShowCreateDialog(true)} data-testid="create-protocol-btn">
                  <Plus size={18} className="mr-2" />
                  Ny inspektion
                </Button>
              )}
              {activeTab === 'actions' && (
                <Button onClick={() => setShowActionDialog(true)} data-testid="create-action-btn">
                  <Plus size={18} className="mr-2" />
                  Registrera åtgärd
                </Button>
              )}
              {activeTab === 'components' && (
                <Button onClick={() => setShowComponentDialog(true)} data-testid="create-component-btn">
                  <Plus size={18} className="mr-2" />
                  Lägg till komponent
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mt-4">
          <Select value={filterProperty} onValueChange={setFilterProperty}>
            <SelectTrigger className="w-[180px]">
              <Buildings size={16} className="mr-2" />
              <SelectValue placeholder="Fastighet" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla fastigheter</SelectItem>
              {properties.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {activeTab === 'inspections' && (
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla</SelectItem>
                <SelectItem value="draft">Utkast</SelectItem>
                <SelectItem value="completed">Slutförda</SelectItem>
              </SelectContent>
            </Select>
          )}
          
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Sök..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Inspections Tab */}
        <TabsContent value="inspections" className="mt-4">
          {filteredProtocols.length === 0 ? (
            <div className="card-flat">
              <div className="empty-state py-12">
                <ClipboardText size={48} className="empty-state-icon" />
                <p className="text-slate-500">Inga inspektioner hittades</p>
                {canEdit && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                    <Plus size={16} className="mr-2" />
                    Skapa första inspektionen
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProtocols.map(protocol => (
                <div 
                  key={protocol.id}
                  className="card-flat hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleOpenInspection(protocol)}
                  data-testid={`protocol-${protocol.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        protocol.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {protocol.status === 'completed' ? (
                          <CheckCircle size={20} className="text-green-600" />
                        ) : (
                          <ClipboardText size={20} className="text-amber-600" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">
                          {getInspectionTypeLabel(protocol.type)}
                        </div>
                        <div className="text-sm text-slate-500 mt-0.5">
                          {protocol.property_id && getPropertyName(protocol.property_id)}
                          {protocol.apartment_id && ` • Lgh ${getApartmentNumber(protocol.apartment_id)}`}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDate(protocol.inspection_date)}
                          </span>
                          {protocol.inspector_name && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {protocol.inspector_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        protocol.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {protocol.status === 'completed' ? 'Slutförd' : 'Utkast'}
                      </span>
                      
                      {protocol.status === 'completed' && (
                        <button
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            exportProtocolToPDF(protocol, { properties, apartments });
                            toast.success('Öppnar utskrift...');
                          }}
                          className="p-2 hover:bg-blue-100 rounded text-slate-400 hover:text-blue-600"
                          title="Skriv ut / PDF"
                        >
                          <Printer size={16} />
                        </button>
                      )}
                      
                      {canEdit && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteProtocol(protocol); }}
                          className="p-2 hover:bg-red-100 rounded text-slate-400 hover:text-red-600"
                        >
                          <Trash size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions" className="mt-4">
          {filteredActions.length === 0 ? (
            <div className="card-flat">
              <div className="empty-state py-12">
                <Wrench size={48} className="empty-state-icon" />
                <p className="text-slate-500">Inga åtgärder registrerade</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActions.map(action => (
                <div key={action.id} className="card-flat" data-testid={`action-${action.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Wrench size={20} className="text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{action.title}</div>
                        <div className="text-sm text-slate-500 mt-0.5">{action.notes}</div>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDate(action.action_date)}
                          </span>
                          {action.performed_by_name && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {action.performed_by_name}
                            </span>
                          )}
                          {action.cost && (
                            <span className="flex items-center gap-1">
                              <CurrencyCircleDollar size={12} />
                              {formatCurrency(action.cost)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Components Tab */}
        <TabsContent value="components" className="mt-4">
          {components.length === 0 ? (
            <div className="card-flat">
              <div className="empty-state py-12">
                <File size={48} className="empty-state-icon" />
                <p className="text-slate-500">Inga komponenter registrerade</p>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {BUILDING_PARTS.map(part => {
                const partComponents = components.filter(c => c.building_part === part.id);
                if (partComponents.length === 0) return null;
                
                return (
                  <div key={part.id} className="card-flat">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xl">{part.icon}</span>
                      <h3 className="font-medium text-slate-900">{part.name}</h3>
                      <span className="text-xs text-slate-500">({partComponents.length})</span>
                    </div>
                    <div className="space-y-2">
                      {partComponents.map(comp => (
                        <div 
                          key={comp.id}
                          className={`p-2 rounded-lg border ${STATUS_CONFIG[comp.status ?? 'ok']?.color || 'bg-slate-50 border-slate-200'}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{comp.name}</span>
                            <span className="text-xs">{STATUS_CONFIG[comp.status ?? 'ok']?.label || comp.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Protocol Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ny inspektion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Typ av inspektion *</Label>
              <Select value={protocolForm.type} onValueChange={(v) => setProtocolForm(prev => ({ ...prev, type: v as InspectionProtocol['type'] }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INSPECTION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fastighet</Label>
                <Select value={protocolForm.property_id || '_none'} onValueChange={(v) => setProtocolForm(prev => ({ ...prev, property_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Välj..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Ingen</SelectItem>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Lägenhet</Label>
                <Select value={protocolForm.apartment_id || '_none'} onValueChange={(v) => setProtocolForm(prev => ({ ...prev, apartment_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Välj..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Ingen</SelectItem>
                    {apartments.map(a => (
                      <SelectItem key={a.id} value={a.id}>Lgh {a.apartment_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={protocolForm.inspection_date}
                  onChange={(e) => setProtocolForm(prev => ({ ...prev, inspection_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Inspektör</Label>
                <Input
                  value={protocolForm.inspector_name}
                  onChange={(e) => setProtocolForm(prev => ({ ...prev, inspector_name: e.target.value }))}
                  placeholder="Namn"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Anteckningar</Label>
              <Textarea
                value={protocolForm.notes}
                onChange={(e) => setProtocolForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Övriga anteckningar..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetProtocolForm(); }}>
                Avbryt
              </Button>
              <Button onClick={handleCreateProtocol} disabled={saving}>
                {saving ? 'Skapar...' : 'Skapa'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inspection Dialog (Edit/Fill) */}
      <Dialog open={showInspectionDialog} onOpenChange={setShowInspectionDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{getInspectionTypeLabel(protocolForm.type)}</span>
              <span className={`text-sm px-2 py-1 rounded-full ${
                selectedProtocol?.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
              }`}>
                {selectedProtocol?.status === 'completed' ? 'Slutförd' : 'Utkast'}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Info row */}
            <div className="flex flex-wrap gap-4 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
              <span className="flex items-center gap-1">
                <Calendar size={14} />
                {formatDate(protocolForm.inspection_date)}
              </span>
              {protocolForm.property_id && (
                <span className="flex items-center gap-1">
                  <Buildings size={14} />
                  {getPropertyName(protocolForm.property_id)}
                </span>
              )}
              {protocolForm.apartment_id && (
                <span className="flex items-center gap-1">
                  <House size={14} />
                  Lgh {getApartmentNumber(protocolForm.apartment_id)}
                </span>
              )}
              {protocolForm.inspector_name && (
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {protocolForm.inspector_name}
                </span>
              )}
            </div>
            
            {/* Building parts checklist */}
            <div className="space-y-4">
              {protocolForm.items?.map((item, index) => {
                const itemStatus = item.status ?? 'ok';
                const StatusIcon = STATUS_CONFIG[itemStatus]?.icon || CheckCircle;
                
                return (
                  <div key={item.building_part} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">
                          {BUILDING_PARTS.find(p => p.id === item.building_part)?.icon}
                        </span>
                        <h4 className="font-medium text-slate-900">{item.building_part_name}</h4>
                      </div>
                      
                      <Select 
                        value={itemStatus} 
                        onValueChange={(v) => updateProtocolItem(index, 'status', v)}
                        disabled={selectedProtocol?.status === 'completed'}
                      >
                        <SelectTrigger className={`w-[160px] ${STATUS_CONFIG[itemStatus]?.color}`}>
                          <StatusIcon size={14} className="mr-1" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Kommentar</Label>
                        <Textarea
                          value={item.comment}
                          onChange={(e) => updateProtocolItem(index, 'comment', e.target.value)}
                          placeholder="Beskrivning av skick..."
                          className="mt-1"
                          rows={2}
                          disabled={selectedProtocol?.status === 'completed'}
                        />
                      </div>
                      
                      <div>
                        <Label className="text-xs">Rekommenderad åtgärd</Label>
                        <Textarea
                          value={item.recommended_action}
                          onChange={(e) => updateProtocolItem(index, 'recommended_action', e.target.value)}
                          placeholder="Vad behöver göras..."
                          className="mt-1"
                          rows={2}
                          disabled={selectedProtocol?.status === 'completed'}
                        />
                      </div>
                    </div>
                    
                    {(item.status === 'action_required' || item.status === 'urgent') && (
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <Label className="text-xs">Prioritet</Label>
                          <Select 
                            value={item.priority} 
                            onValueChange={(v) => updateProtocolItem(index, 'priority', v)}
                            disabled={selectedProtocol?.status === 'completed'}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                                <SelectItem key={key} value={key}>{config.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label className="text-xs">Uppskattad kostnad (kr)</Label>
                          <Input
                            type="number"
                            value={item.estimated_cost}
                            onChange={(e) => updateProtocolItem(index, 'estimated_cost', e.target.value)}
                            placeholder="0"
                            className="mt-1"
                            disabled={selectedProtocol?.status === 'completed'}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Image Upload Section */}
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs flex items-center gap-1">
                          <Camera size={12} />
                          Bilder ({item.images?.length || 0})
                        </Label>
                        
                        {selectedProtocol?.status !== 'completed' && (
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageUpload(index, file);
                              }}
                              disabled={uploadingImage === index}
                            />
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${
                              uploadingImage === index 
                                ? 'bg-slate-100 text-slate-400 cursor-wait' 
                                : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200'
                            }`}>
                              {uploadingImage === index ? (
                                <>Laddar upp...</>
                              ) : (
                                <>
                                  <CloudArrowUp size={14} />
                                  Lägg till bild
                                </>
                              )}
                            </span>
                          </label>
                        )}
                      </div>
                      
                      {/* Image Gallery */}
                      {(item.images?.length ?? 0) > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {item.images?.map((img, imgIndex) => (
                            <div key={imgIndex} className="relative group">
                              <a 
                                href={img.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img 
                                  src={img.url} 
                                  alt={img.name || `Bild ${imgIndex + 1}`}
                                  className="w-20 h-20 object-cover rounded-lg border border-slate-200 hover:border-slate-400 transition-colors"
                                />
                              </a>
                              {selectedProtocol?.status !== 'completed' && (
                                <button
                                  onClick={() => removeImage(index, imgIndex)}
                                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Ta bort bild"
                                >
                                  <X size={12} weight="bold" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Actions */}
            {selectedProtocol?.status !== 'completed' && (
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowInspectionDialog(false); setSelectedProtocol(null); }}>
                  Avbryt
                </Button>
                <Button variant="outline" onClick={() => handleSaveInspection(false)} disabled={saving}>
                  Spara utkast
                </Button>
                <Button onClick={() => handleSaveInspection(true)} disabled={saving} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle size={16} className="mr-2" />
                  {saving ? 'Sparar...' : 'Slutför & skapa åtgärder'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Registrera åtgärd</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={actionForm.title}
                onChange={(e) => setActionForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="t.ex. Målning av fasad"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={actionForm.action_date}
                  onChange={(e) => setActionForm(prev => ({ ...prev, action_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Utfört av</Label>
                <Input
                  value={actionForm.performed_by_name}
                  onChange={(e) => setActionForm(prev => ({ ...prev, performed_by_name: e.target.value }))}
                  placeholder="Namn"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fastighet</Label>
                <Select value={actionForm.property_id || '_none'} onValueChange={(v) => setActionForm(prev => ({ ...prev, property_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Välj..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Ingen</SelectItem>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Kostnad (kr)</Label>
                <Input
                  type="number"
                  value={actionForm.cost}
                  onChange={(e) => setActionForm(prev => ({ ...prev, cost: e.target.value }))}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Beskrivning</Label>
              <Textarea
                value={actionForm.description}
                onChange={(e) => setActionForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Vad gjordes..."
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowActionDialog(false); resetActionForm(); }}>
                Avbryt
              </Button>
              <Button onClick={handleCreateAction} disabled={saving}>
                {saving ? 'Sparar...' : 'Registrera'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Component Dialog */}
      <Dialog open={showComponentDialog} onOpenChange={setShowComponentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Lägg till komponent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Namn *</Label>
              <Input
                value={componentForm.name}
                onChange={(e) => setComponentForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="t.ex. Träpanel"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Byggnadsdel *</Label>
              <Select value={componentForm.building_part} onValueChange={(v) => setComponentForm(prev => ({ ...prev, building_part: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUILDING_PARTS.map(part => (
                    <SelectItem key={part.id} value={part.id}>
                      {part.icon} {part.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Material</Label>
                <Input
                  value={componentForm.material}
                  onChange={(e) => setComponentForm(prev => ({ ...prev, material: e.target.value }))}
                  placeholder="t.ex. Furu"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Installationsår</Label>
                <Input
                  type="number"
                  value={componentForm.installation_year}
                  onChange={(e) => setComponentForm(prev => ({ ...prev, installation_year: e.target.value }))}
                  placeholder="t.ex. 2010"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Placering</Label>
              <Input
                value={componentForm.location}
                onChange={(e) => setComponentForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="t.ex. Norra fasaden"
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowComponentDialog(false); resetComponentForm(); }}>
                Avbryt
              </Button>
              <Button onClick={handleCreateComponent} disabled={saving}>
                {saving ? 'Sparar...' : 'Lägg till'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
